
import { useState } from "react";
import { Play, PlayCircle, AlertCircle, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { startCampaign, simulateCampaignResults, startRealCampaign } from "@/services/campaigns";
import { toast } from "@/hooks/use-toast";
import { Lead } from "@/types";

interface CampaignActionsProps {
  selectedLeads: Set<string>;
  fetchLeads: () => Promise<void>;
  allLeads: Lead[]; 
}

const CampaignActions = ({ selectedLeads, fetchLeads, allLeads }: CampaignActionsProps) => {
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [scriptTemplate, setScriptTemplate] = useState(
    "Hi there, this is a call from your car dealership. We noticed you were interested in one of our vehicles recently and wanted to follow up. If you're still interested, please press 1 or call us back at your convenience."
  );
  const [showScriptEditor, setShowScriptEditor] = useState(false);

  const handleStartCampaign = async (demo: boolean = false) => {
    if (selectedLeads.size === 0) {
      toast({
        title: "No leads selected",
        description: "Please select at least one lead to start a campaign.",
        variant: "destructive",
      });
      return;
    }

    setIsStarting(true);

    try {
      const leadIds = Array.from(selectedLeads);
      let result;
      
      if (demo) {
        // Start campaign and immediately simulate results for demo
        const { success, error } = await startCampaign(leadIds);
        if (!success) throw new Error(error);
        
        result = await simulateCampaignResults(leadIds);
      } else {
        // For real campaigns, use the selected leads with the script template
        const selectedLeadObjects = allLeads.filter(lead => lead.id && selectedLeads.has(lead.id));
        
        result = await startRealCampaign(
          selectedLeadObjects,
          scriptTemplate
        );
      }

      if (result.success) {
        toast({
          title: demo ? "Demo campaign completed" : "Campaign started",
          description: demo 
            ? "Demo results have been generated for the selected leads." 
            : "The campaign has been started for the selected leads. AI will personalize each call based on lead information.",
        });
        fetchLeads();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Campaign error:", error);
      toast({
        title: "Campaign error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsStarting(false);
      setShowCampaignDialog(false);
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="sm"
        onClick={() => setShowCampaignDialog(true)}
        disabled={selectedLeads.size === 0}
        className="flex items-center gap-1"
      >
        <Play className="h-4 w-4" />
        <span>Start Campaign</span>
      </Button>

      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-md md:max-w-lg">
          <DialogHeader>
            <DialogTitle>Start Campaign</DialogTitle>
            <DialogDescription>
              Start a calling campaign for {selectedLeads.size} selected lead{selectedLeads.size !== 1 ? 's' : ''}. 
              Calls will be queued and processed with AI personalization.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Base Call Script</div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowScriptEditor(!showScriptEditor)}
              className="flex items-center gap-1"
            >
              <Edit className="h-3 w-3" />
              <span>{showScriptEditor ? "Hide" : "Edit"}</span>
            </Button>
          </div>
          
          {showScriptEditor ? (
            <Textarea
              value={scriptTemplate}
              onChange={(e) => setScriptTemplate(e.target.value)}
              placeholder="Enter the base script for your calls..."
              className="min-h-[120px]"
            />
          ) : (
            <div className="text-sm p-3 border rounded-md bg-muted/50 max-h-[80px] overflow-y-auto">
              {scriptTemplate}
            </div>
          )}

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>AI-Powered Personalization</AlertTitle>
            <AlertDescription className="text-xs">
              Our AI will analyze each lead's information to personalize this script before the call,
              making the outreach warmer and more effective.
            </AlertDescription>
          </Alert>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCampaignDialog(false)}
              disabled={isStarting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleStartCampaign(true)}
              disabled={isStarting}
              className="gap-2"
            >
              <PlayCircle className="h-4 w-4" />
              Run Demo Campaign
            </Button>
            <Button
              onClick={() => handleStartCampaign(false)}
              disabled={isStarting}
              className="gap-2"
            >
              <Play className="h-4 w-4" />
              Start Real Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CampaignActions;
